import React, { useState } from 'react';
import styled from 'astroturf/react';

import { Banner } from 'js/design_system/Banner';
import { Icon } from 'js/design_system/Icon';
import { Body } from 'js/design_system/Body';
import { Heading } from 'js/design_system/Heading';
import {
  getProfileBulkDownloadFileList,
  getBulkDownloadServerSideZipUrl,
} from 'js/bulk_download/bulk_download_helper';
import _Card from "ProfilePdfPackCard-Card.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=Card";
import _Header from "ProfilePdfPackCard-Header.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=Header";
import _Title from "ProfilePdfPackCard-Title.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=Title";
import _Description from "ProfilePdfPackCard-Description.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=Description";
import _StatsContainer from "ProfilePdfPackCard-StatsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=StatsContainer";
import _Stat from "ProfilePdfPackCard-Stat.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=Stat";
import _StatLabel from "ProfilePdfPackCard-StatLabel.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=StatLabel";
import _StatValue from "ProfilePdfPackCard-StatValue.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=StatValue";
import _IconWrapper from "ProfilePdfPackCard-IconWrapper.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/ProfilePdfPackCard.tsx?styleId=IconWrapper";


const Card = /*#__PURE__*/styled("div", null, {
  displayName: "Card",
  styles: _Card
});

const Header = /*#__PURE__*/styled("div", null, {
  displayName: "Header",
  styles: _Header
});

const Title = /*#__PURE__*/styled(Heading, null, {
  displayName: "Title",
  styles: _Title
});

const Description = /*#__PURE__*/styled(Body, null, {
  displayName: "Description",
  styles: _Description
});

const StatsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "StatsContainer",
  styles: _StatsContainer
});

const Stat = /*#__PURE__*/styled("div", null, {
  displayName: "Stat",
  styles: _Stat
});

const StatLabel = /*#__PURE__*/styled(Body, null, {
  displayName: "StatLabel",
  styles: _StatLabel
});

const StatValue = /*#__PURE__*/styled(Heading, null, {
  displayName: "StatValue",
  styles: _StatValue
});

const IconWrapper = /*#__PURE__*/styled("div", null, {
  displayName: "IconWrapper",
  styles: _IconWrapper
});

interface Props {
  viewedUserId: number;
  viewedUserDisplayName: string;
  worksCount?: number;
}

export const ProfilePdfPackCard: React.FC<Props> = ({
  viewedUserId,
  viewedUserDisplayName,
  worksCount = 0,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownloadClick = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await getProfileBulkDownloadFileList(viewedUserId);

      if (!data.files || data.files.length === 0) {
        setError('No downloadable papers found for this author.');
        return;
      }

      // Filter out any files with missing URLs
      const validFiles = data.files.filter((f) => f.url && f.filename);

      if (validFiles.length === 0) {
        setError('No downloadable papers found for this author.');
        return;
      }

      const zipUrl = await getBulkDownloadServerSideZipUrl(
        validFiles,
        `${viewedUserDisplayName}_papers`,
        data.zipperUrl,
      );

      if (!zipUrl) {
        setError('Failed to create download package. Please try again.');
        return;
      }

      // Trigger download
      const link = document.createElement('a');
      link.href = zipUrl;
      link.download = `${viewedUserDisplayName}_papers.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading profile PDF pack:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (worksCount === 0) {
    return null;
  }

  return (
    <Card>
      <Header>
        <IconWrapper>
          <Icon name="deployed_code_update" size={32} />
        </IconWrapper>
        <Title size="sm">Download All Papers by {viewedUserDisplayName}</Title>
      </Header>

      <Description size="medium">
        Get instant access to all papers and related research in one PDF
        package.
      </Description>

      <StatsContainer>
        <Stat>
          <StatLabel size="small">Papers by Author</StatLabel>
          <StatValue size="xs">{worksCount}</StatValue>
        </Stat>
        <Stat>
          <StatLabel size="small">Related Papers</StatLabel>
          <StatValue size="xs">Up to 20</StatValue>
        </Stat>
      </StatsContainer>

      {error && (
        <Banner
          type="danger"
          border="all"
          icon={<Icon name="error" size={24} />}
        >
          {error}
        </Banner>
      )}

      <Banner
        type="info"
        border="all"
        icon={<Icon name="info" size={24} />}
        actions={[
          {
            content: isLoading
              ? 'Preparing Download...'
              : 'Download PDF Package',
            onClick: handleDownloadClick,
          },
        ]}
      >
        This feature is available to Premium members
      </Banner>
    </Card>
  );
};
